import api from "./api";

export interface ISharePointFile {
    id: string;
    name: string;
    webUrl: string;
    lastModified: string;
    lastModifiedBy?: string;
    size: number;
    contentType: string;
}

export interface IExcelPreview {
    projectInputData: Array<Record<string, any>>;
    salesInputData: Array<Record<string, any>>;
    projectInputColumns: string[];
    salesInputColumns: string[];
    snapshots?: Record<string, string>;
}

export interface ISnapshot {
    project_name: string;
    file_name: string;
    blob_url: string;
    created_at: string;
    modified_on: string;
    sheet_name: string;
  }

export const getSharePointExcelFiles = async (): Promise<ISharePointFile[]> => {
    try {
        const response = await api.get('/business-cases/sharepoint-files/');
        
        if (response.data && response.data.results) {
            const files = response.data.results;
            return files;
        }
        
        console.warn('No results found in SharePoint response');
        return [];
    } catch (error) {
        console.error('Error fetching SharePoint files:', error);
        
        // Return mock data for development if API fails
        const mockFiles: ISharePointFile[] = [
            {
                id: 'mock-1',
                name: 'Business_Case_Template_2024.xlsx',
                webUrl: 'https://mock-url.com/file1.xlsx',
                lastModified: '2024-01-15T10:30:00Z',
                size: 156789,
                contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            },
            {
                id: 'mock-2',
                name: 'Q4_Financial_Model.xlsx',
                webUrl: 'https://mock-url.com/file2.xlsx',
                lastModified: '2024-01-10T14:20:00Z',
                size: 234567,
                contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            },
            {
                id: 'mock-3',
                name: 'Project_Analysis_2024.xlsm',
                webUrl: 'https://mock-url.com/file3.xlsm',
                lastModified: '2024-01-08T09:15:00Z',
                size: 445623,
                contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            }
        ];
        
        return mockFiles;
    }
};

export const getExcelPreview = async (file:ISharePointFile) => {
    try {
        const response = await api.post(`/business-cases/preview-snapshot/`,{file:file}, { timeout: 120000 });
        return response;
    } catch (error) {
        console.error('Error fetching Excel preview:', error);
        if (typeof error === 'object' && error !== null && 'code' in error && (error as any).code === 'ECONNABORTED') {
            return { data: { sharepoint_preview_url: file.webUrl } };
}
        throw error;
    }
};

export const getSnapshot = async (blobName: string): Promise<Blob> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000); // 60 second timeout

    try {
        const response = await fetch(
            `/api/business-cases/fetch-snapshot/${encodeURIComponent(blobName)}`,
            { signal: controller.signal }
        );
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
            throw new Error(`Failed to fetch snapshot: ${blobName}`);
        }
        
        return response.blob();
    } catch (error: any) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
            throw new Error(`Snapshot fetch timed out: ${blobName}`);
        }
        throw error;
    }
};

export const processSharePointFile = async (file:ISharePointFile) => {
    try {
        const response = await api.post(`/business-cases/process-file/`,file);
        return response.data;
    } catch (error) {
        console.error('Error processing SharePoint file:', error);
        throw error;
    }
};

export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const formatLastModified = (dateString: string): string => {
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch (error) {
        return dateString;
    }
};